'use client'

import { useEffect, useMemo, useRef, useState, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { createAuthorPost, getAllCategoriesForEditor, getCurrentAuthorComposerProfile } from '@/app/actions/public-actions'
import { ImagePlus, MoreHorizontal, Video } from 'lucide-react'

type AuthorPostComposerProps = {
  profileDisplayName?: string
  avatarUrl?: string | null
}

const AuthorPostComposer = ({ profileDisplayName, avatarUrl }: AuthorPostComposerProps) => {
  const router = useRouter()

  const [resolvedDisplayName, setResolvedDisplayName] = useState<string | null>(
    typeof profileDisplayName === 'string' && profileDisplayName.trim().length > 0 ? profileDisplayName : null,
  )
  const [resolvedAvatarUrl, setResolvedAvatarUrl] = useState<string | null>(
    typeof avatarUrl === 'string' && avatarUrl.trim().length > 0 ? avatarUrl : null,
  )
  const [authChecked, setAuthChecked] = useState(
    typeof profileDisplayName === 'string' && profileDisplayName.trim().length > 0,
  )

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [hashtags, setHashtags] = useState('')
  const [images, setImages] = useState<Array<{ url: string; alt: string }>>([])
  const [videoUrl, setVideoUrl] = useState('')
  const [isVideoOpen, setIsVideoOpen] = useState(false)
  const [isDragOverImage, setIsDragOverImage] = useState(false)
  const [openImageMenuUrl, setOpenImageMenuUrl] = useState<string | null>(null)
  const [editingAltUrl, setEditingAltUrl] = useState<string | null>(null)
  const [editingAltText, setEditingAltText] = useState('')
  const [categoryOptions, setCategoryOptions] = useState<Array<{ slug: string; name: string }>>([])
  const [selectedCategories, setSelectedCategories] = useState<Array<{ slug: string; name: string }>>([])
  const [tagQuery, setTagQuery] = useState<{ start: number; end: number; query: string } | null>(null)
  const [activeSuggestion, setActiveSuggestion] = useState(0)

  const [isExpanded, setIsExpanded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isPending, startTransition] = useTransition()

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const hashtagInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (authChecked) return
    let cancelled = false
    ;(async () => {
      try {
        const profile = await getCurrentAuthorComposerProfile()
        if (cancelled) return
        if (!profile) {
          setResolvedDisplayName(null)
          setResolvedAvatarUrl(null)
          setAuthChecked(true)
          return
        }
        const displayName = String(profile.display_name || '').trim() || 'Author'
        const avatar = typeof profile.avatar_url === 'string' && profile.avatar_url.trim().length > 0 ? profile.avatar_url : null
        setResolvedDisplayName(displayName)
        setResolvedAvatarUrl(avatar)
        setAuthChecked(true)
      } catch {
        if (cancelled) return
        setResolvedDisplayName(null)
        setResolvedAvatarUrl(null)
        setAuthChecked(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authChecked])

  const safeDisplayName = typeof resolvedDisplayName === 'string' ? resolvedDisplayName : ''
  const hasAvatar = typeof resolvedAvatarUrl === 'string' && resolvedAvatarUrl.trim().length > 0
  const initials =
    safeDisplayName
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase() || safeDisplayName.slice(0, 2).toUpperCase()

  const resetForm = () => {
    setTitle('')
    setBody('')
    setHashtags('')
    setImages([])
    setSelectedCategories([])
    setVideoUrl('')
    setIsVideoOpen(false)
    setOpenImageMenuUrl(null)
    setEditingAltUrl(null)
    setEditingAltText('')
    setError(null)
    setUploadError(null)
    setIsExpanded(false)
  }

  useEffect(() => {
    if (!isExpanded) return
    let cancelled = false
    ;(async () => {
      try {
        const categories = await getAllCategoriesForEditor()
        if (!cancelled) {
          setCategoryOptions(Array.isArray(categories) ? categories : [])
        }
      } catch {
        if (!cancelled) setCategoryOptions([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isExpanded])

  useEffect(() => {
    if (!openImageMenuUrl) return
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return
      if (target.closest('[data-image-menu="true"]')) return
      setOpenImageMenuUrl(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [openImageMenuUrl])

  const extractedHashtagSlugs = useMemo(() => {
    const text = hashtags.trim()
    if (!text) return []
    const out: string[] = []
    const seen = new Set<string>()
    const re = /(^|\s)#([a-z0-9-]{1,64})/gi
    let m: RegExpExecArray | null = null
    while ((m = re.exec(text))) {
      const slug = String(m[2] || '').trim()
      if (!slug) continue
      const key = slug.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push(slug)
    }
    return out
  }, [hashtags])

  useEffect(() => {
    if (!isExpanded) return
    if (extractedHashtagSlugs.length === 0) {
      setSelectedCategories([])
      return
    }
    const next = extractedHashtagSlugs.map(slug => {
      const key = slug.toLowerCase()
      const match = categoryOptions.find(c => String(c.slug || '').toLowerCase() === key)
      if (match) return { slug: match.slug, name: match.name }
      return { slug, name: slug }
    })
    setSelectedCategories(next)
  }, [isExpanded, extractedHashtagSlugs, categoryOptions])

  const hashtagCategories = useMemo(() => {
    if (!tagQuery || tagQuery.query.trim().length === 0) return []
    const q = tagQuery.query.trim().toLowerCase()
    const matches = categoryOptions.filter(c => {
      const slug = String(c.slug || '').toLowerCase()
      const name = String(c.name || '').toLowerCase()
      const already = extractedHashtagSlugs.some(s => s.toLowerCase() === slug)
      if (already) return false
      return slug.startsWith(q) || name.startsWith(q) || slug.includes(q) || name.includes(q)
    })
    return matches.slice(0, 8)
  }, [tagQuery, categoryOptions, extractedHashtagSlugs])

  const updateTagQuery = (text: string, cursor: number) => {
    const before = text.slice(0, cursor)
    const hashIndex = before.lastIndexOf('#')
    if (hashIndex === -1) {
      setTagQuery(null)
      return
    }
    if (hashIndex > 0 && !/\s/.test(before[hashIndex - 1])) {
      setTagQuery(null)
      return
    }
    const query = before.slice(hashIndex + 1)
    if (!/^[a-z0-9_-]*$/i.test(query)) {
      setTagQuery(null)
      return
    }
    if (query.length < 1) {
      setTagQuery(null)
      return
    }
    setActiveSuggestion(0)
    setTagQuery({ start: hashIndex, end: cursor, query })
  }

  const selectCategory = (category: { slug: string; name: string }) => {
    if (!tagQuery) return
    const insertion = `#${category.slug} `
    const next = hashtags.slice(0, tagQuery.start) + insertion + hashtags.slice(tagQuery.end)
    setHashtags(next)
    setTagQuery(null)
    setActiveSuggestion(0)
    requestAnimationFrame(() => {
      const el = hashtagInputRef.current
      if (!el) return
      const nextPos = tagQuery.start + insertion.length
      el.focus()
      el.setSelectionRange(nextPos, nextPos)
    })
  }

  const removeSelectedCategory = (slug: string) => {
    const key = String(slug || '').toLowerCase()
    setHashtags(prev =>
      prev
        .replace(new RegExp(`(^|\\s)#${key}(?=\\s|$)`, 'gi'), '$1')
        .replace(/[ \t]{2,}/g, ' ')
        .trim(),
    )
    setSelectedCategories(prev => prev.filter(c => String(c.slug || '').toLowerCase() !== key))
  }

  const extractYouTubeVideoId = (input: string): string | null => {
    const raw = String(input || '').trim()
    if (!raw) return null
    let url: URL | null = null
    try {
      url = new URL(raw)
    } catch {
      try {
        url = new URL(`https://${raw}`)
      } catch {
        url = null
      }
    }
    if (!url) return null

    const host = url.hostname.replace(/^www\./i, '').toLowerCase()
    const parts = url.pathname.split('/').filter(Boolean)
    const isValidId = (id: string) => /^[a-zA-Z0-9_-]{11}$/.test(id)

    if (host === 'youtu.be') {
      const id = parts[0] || ''
      return isValidId(id) ? id : null
    }

    if (host === 'youtube.com' || host === 'm.youtube.com' || host.endsWith('.youtube.com')) {
      if (url.pathname === '/watch') {
        const id = url.searchParams.get('v') || ''
        return isValidId(id) ? id : null
      }
      const first = parts[0] || ''
      const second = parts[1] || ''
      if (first === 'embed' || first === 'shorts' || first === 'live' || first === 'v') {
        const id = second || ''
        return isValidId(id) ? id : null
      }
    }

    return null
  }

  const buildYouTubeEmbedUrl = (id: string) => {
    const videoId = String(id || '').trim()
    if (!videoId) return ''
    const params = new URLSearchParams({
      autoplay: '0',
      mute: '0',
      rel: '0',
      modestbranding: '1',
      origin: 'https://carsite.co.za',
      widget_referrer: 'https://carsite.co.za',
    })
    return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)

    const trimmedTitle = title.trim()
    const trimmedBody = body.trim()

    if (!trimmedTitle || !trimmedBody) {
      setError('Please add a title and a short description for your post.')
      return
    }

    const categories = (() => {
      const out: Array<{ slug: string; name: string }> = []
      const seen = new Set<string>()

      for (const c of selectedCategories) {
        const slug = String(c.slug || '').trim()
        const name = String(c.name || c.slug || '').trim() || slug
        const key = slug.toLowerCase()
        if (!slug || seen.has(key)) continue
        seen.add(key)
        out.push({ slug, name })
      }

      return out
    })()

    const cleanedBody = trimmedBody
      .replace(/(^|\s)#[a-z0-9-]{1,64}(?=\s|$)/gi, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim()

    if (!cleanedBody) {
      setError('Please add a short description for your post.')
      return
    }

    startTransition(async () => {
      const res = await createAuthorPost({
        title: trimmedTitle,
        body: cleanedBody,
        images: images.map(i => i.url),
        imageMeta: images.map(i => ({ url: i.url, alt: i.alt })),
        categories,
        videoUrl: videoUrl.trim() || undefined,
      })

      if (!res || !res.success) {
        setError(res?.error || 'Something went wrong while creating your post.')
        return
      }

      resetForm()
      setSuccessMessage('Published.')
      router.refresh()
    })
  }

  const handleFileButtonClick = () => {
    setIsExpanded(true)
    fileInputRef.current?.click()
  }

  const openAltEditor = (img: { url: string; alt: string }) => {
    setEditingAltUrl(img.url)
    setEditingAltText(img.alt || '')
  }

  const uploadFiles = async (files: File[]) => {
    const existingCount = images.length
    const remainingSlots = Math.max(0, 10 - existingCount)
    const filesToUpload = files.slice(0, remainingSlots)

    if (filesToUpload.length === 0) {
      setUploadError('You can attach up to 10 images.')
      return
    }

    setIsUploading(true)

    try {
      const uploaded: string[] = []

      for (const file of filesToUpload) {
        const formData = new FormData()
        formData.append('file', file)

        const res = await fetch('/api/upload', {
          method: 'POST',
          body: formData,
        })

        if (!res.ok) {
          continue
        }

        const json = (await res.json()) as { success?: boolean; url?: string }
        if (json?.success && json.url) {
          uploaded.push(json.url)
        }
      }

      if (uploaded.length === 0) {
        setUploadError('Image upload failed. Please try again.')
      } else {
        setImages(prev => [...prev, ...uploaded.map(url => ({ url, alt: '' }))].slice(0, 10))
      }
    } catch {
      setUploadError('Image upload failed. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setUploadError(null)

    const imageFiles = Array.from(files).filter(f => String(f.type || '').toLowerCase().startsWith('image/'))
    await uploadFiles(imageFiles)
    event.target.value = ''
  }

  const handleRemoveImage = (url: string) => {
    setImages(prev => prev.filter(img => img.url !== url))
  }

  if (!isExpanded) {
    return (
      <div className="mb-8 rounded-2xl border border-[var(--color-border)] bg-[var(--bg-elevated)] p-3 md:p-4">
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="flex w-full items-center gap-3"
        >
          <div className="h-9 w-9 rounded-full bg-[var(--bg-secondary)] overflow-hidden flex items-center justify-center text-xs font-bold text-[var(--color-text-muted)]">
            {hasAvatar ? (
              <img
                src={resolvedAvatarUrl as string}
                alt={resolvedDisplayName || ''}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div className="flex-1">
            <div className="w-full rounded-full border border-[var(--color-border)] bg-[var(--bg-base)] px-4 py-2 text-left text-sm text-[var(--color-text-muted)] hover:bg-[var(--bg-secondary)] transition-colors">
              {`What's on your mind, ${resolvedDisplayName}?`}
            </div>
          </div>
        </button>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleFileButtonClick}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
            aria-label="Add photos"
          >
            <ImagePlus size={16} />
          </button>
          <button
            type="button"
            onClick={() => {
              setIsExpanded(true)
              setIsVideoOpen(true)
            }}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
            aria-label="Add video"
          >
            <Video size={16} />
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    )
  }

  if (!authChecked || !safeDisplayName) return null

  return (
    <div className="mb-8 rounded-2xl border border-[var(--color-border)] bg-[var(--bg-elevated)] p-4 md:p-5">
      <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in">
        <div className="w-full space-y-3">
          <div
            className={`relative w-full overflow-hidden rounded-xl border bg-[var(--bg-base)] aspect-[16/9] ${
              isDragOverImage ? 'border-[var(--color-primary)]' : 'border-[var(--color-border)]'
            }`}
            onDragEnter={e => {
              if (isUploading) return
              if (!e.dataTransfer?.types?.includes('Files')) return
              e.preventDefault()
              setIsDragOverImage(true)
            }}
            onDragOver={e => {
              if (isUploading) return
              if (!e.dataTransfer?.types?.includes('Files')) return
              e.preventDefault()
              setIsDragOverImage(true)
            }}
            onDragLeave={() => setIsDragOverImage(false)}
            onDrop={async e => {
              if (isUploading) return
              e.preventDefault()
              setIsDragOverImage(false)
              setUploadError(null)
              const files = Array.from(e.dataTransfer.files || []).filter(f =>
                String(f.type || '').toLowerCase().startsWith('image/'),
              )
              await uploadFiles(files)
            }}
          >
            {images.length > 0 ? (
              <img
                src={images[0].url}
                alt={images[0].alt || 'Attached'}
                className="w-full h-full object-cover"
              />
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`h-full w-full flex items-center justify-center text-sm text-[var(--color-text-muted)] transition-colors ${
                  isDragOverImage ? 'bg-[var(--bg-secondary)]' : 'hover:bg-[var(--bg-secondary)]'
                }`}
              >
                {isDragOverImage ? 'Drop images here' : 'Add an image'}
              </button>
            )}
            {images.length > 0 && (
              <div className="absolute top-2 right-2" data-image-menu="true">
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    setOpenImageMenuUrl(prev => (prev === images[0].url ? null : images[0].url))
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white"
                  aria-label="Image options"
                >
                  <MoreHorizontal size={16} />
                </button>
                {openImageMenuUrl === images[0].url && (
                  <div className="absolute right-0 mt-2 w-44 rounded-xl border border-[var(--color-border)] bg-[var(--bg-elevated)] shadow-lg overflow-hidden z-50">
                    <button
                      type="button"
                      onClick={() => {
                        openAltEditor(images[0])
                        setOpenImageMenuUrl(null)
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-[var(--color-text-main)] hover:bg-[var(--bg-secondary)] transition-colors"
                    >
                      Edit alt text
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleRemoveImage(images[0].url)
                        setOpenImageMenuUrl(null)
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-[var(--bg-secondary)] transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {images.slice(1).map(img => (
                <div
                  key={img.url}
                  className="relative aspect-square rounded-lg overflow-hidden border border-[var(--color-border)] bg-[var(--bg-secondary)]"
                >
                  <img src={img.url} alt={img.alt || 'Attached'} className="w-full h-full object-cover" />
                  <div className="absolute top-1 right-1" data-image-menu="true">
                    <button
                      type="button"
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        setOpenImageMenuUrl(prev => (prev === img.url ? null : img.url))
                      }}
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white"
                      aria-label="Image options"
                    >
                      <MoreHorizontal size={14} />
                    </button>
                    {openImageMenuUrl === img.url && (
                      <div className="absolute right-0 mt-2 w-44 rounded-xl border border-[var(--color-border)] bg-[var(--bg-elevated)] shadow-lg overflow-hidden z-50">
                        <button
                          type="button"
                          onClick={() => {
                            openAltEditor(img)
                            setOpenImageMenuUrl(null)
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-[var(--color-text-main)] hover:bg-[var(--bg-secondary)] transition-colors"
                        >
                          Edit alt text
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            handleRemoveImage(img.url)
                            setOpenImageMenuUrl(null)
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-[var(--bg-secondary)] transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {editingAltUrl && (
          <div
            className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
            onMouseDown={e => {
              if (e.target === e.currentTarget) {
                setEditingAltUrl(null)
                setEditingAltText('')
              }
            }}
          >
            <div className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--bg-elevated)] p-4">
              <div className="text-sm font-semibold text-[var(--color-text-main)]">Edit alt text</div>
              <div className="mt-3">
                <label htmlFor="alt-text-editor" className="sr-only">Alt text</label>
                <input
                  id="alt-text-editor"
                  name="alt_text"
                  type="text"
                  value={editingAltText}
                  onChange={e => setEditingAltText(e.target.value)}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--bg-base)] px-3.5 py-2.5 text-sm text-[var(--color-text-main)] focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] outline-none transition-colors"
                  placeholder="Describe the image"
                  autoFocus
                />
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditingAltUrl(null)
                    setEditingAltText('')
                  }}
                  className="btn btn-outline text-xs px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const url = editingAltUrl
                    if (!url) return
                    setImages(prev =>
                      prev.map(i => (i.url === url ? { ...i, alt: editingAltText.trim() } : i)),
                    )
                    setEditingAltUrl(null)
                    setEditingAltText('')
                  }}
                  className="btn btn-primary text-xs px-4 py-2"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-[var(--bg-secondary)] overflow-hidden flex items-center justify-center text-xs font-bold text-[var(--color-text-muted)] mt-0.5">
            {hasAvatar ? (
              <img
                src={resolvedAvatarUrl as string}
                alt={resolvedDisplayName || ''}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div className="flex-1 space-y-2">
            <label htmlFor="post-title" className="sr-only">Post Title</label>
            <input
              id="post-title"
              name="title"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={`What would you like to share, ${resolvedDisplayName}?`}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--bg-base)] px-3.5 py-2.5 text-sm text-[var(--color-text-main)] focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] outline-none transition-colors"
              maxLength={180}
            />
            <div className="relative isolate z-20">
              <label htmlFor="post-body" className="sr-only">Post Body</label>
              <textarea
                id="post-body"
                name="body"
                value={body}
                onChange={e => {
                  const nextBody = e.target.value
                  setBody(nextBody)
                }}
                rows={3}
                placeholder="Add a short description, thoughts on a recent drive, or quick review."
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--bg-base)] px-3.5 py-2.5 text-sm text-[var(--color-text-main)] focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] outline-none transition-colors resize-none"
              />
            </div>
            <div className="relative isolate z-20">
              <label htmlFor="post-hashtags" className="sr-only">Post Hashtags</label>
              <input
                id="post-hashtags"
                name="hashtags"
                ref={hashtagInputRef}
                value={hashtags}
                onChange={e => {
                  const next = e.target.value
                  setHashtags(next)
                  updateTagQuery(next, e.target.selectionStart || 0)
                }}
                onKeyDown={e => {
                  if (!tagQuery || hashtagCategories.length === 0) return
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    setTagQuery(null)
                    return
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setActiveSuggestion(i => Math.min(i + 1, hashtagCategories.length - 1))
                    return
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setActiveSuggestion(i => Math.max(i - 1, 0))
                    return
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    const picked = hashtagCategories[activeSuggestion]
                    if (picked) selectCategory(picked)
                  }
                }}
                placeholder="Hashtags (e.g. #suv #toyota)"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--bg-base)] px-3.5 py-2.5 text-sm text-[var(--color-text-main)] focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] outline-none transition-colors"
              />
              {tagQuery && hashtagCategories.length > 0 && (
                <div className="absolute left-0 right-0 mt-2 rounded-xl border border-[var(--color-border)] bg-[var(--bg-elevated)] shadow-lg overflow-hidden z-50">
                  <div className="max-h-56 overflow-auto">
                    {hashtagCategories.map((c, idx) => (
                      <button
                        key={c.slug}
                        type="button"
                        onMouseDown={e => {
                          e.preventDefault()
                          selectCategory(c)
                        }}
                        className={`w-full px-4 py-3 text-left flex items-center justify-between gap-3 text-sm ${
                          idx === activeSuggestion
                            ? 'bg-[var(--bg-secondary)]'
                            : 'hover:bg-[var(--bg-secondary)]'
                        }`}
                      >
                        <span className="font-semibold text-[var(--color-text-main)]">#{c.slug}</span>
                        <span className="text-xs text-[var(--color-text-muted)] truncate">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {(isVideoOpen || videoUrl.trim().length > 0) && (
              <div className="space-y-2">
                <label htmlFor="post-video" className="sr-only">Video URL</label>
                <input
                  id="post-video"
                  name="video_url"
                  type="url"
                  value={videoUrl}
                  onChange={e => setVideoUrl(e.target.value)}
                  placeholder="YouTube video URL"
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--bg-base)] px-3.5 py-2.5 text-sm text-[var(--color-text-main)] focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] outline-none transition-colors"
                />
                {(() => {
                  const id = extractYouTubeVideoId(videoUrl)
                  if (!id) return null
                  return (
                    <div className="aspect-video w-full bg-[var(--bg-secondary)] rounded border border-[var(--color-border)] overflow-hidden relative">
                      <iframe
                        src={buildYouTubeEmbedUrl(id)}
                        title="Featured Video Preview"
                        className="absolute inset-0 w-full h-full"
                        allow="autoplay; encrypted-media; picture-in-picture"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </div>
                  )
                })()}
              </div>
            )}
          </div>
        </div>

        {uploadError && (
          <p className="text-xs text-red-500">
            {uploadError}
          </p>
        )}

        {successMessage && (
          <p className="text-xs text-green-500">
            {successMessage}
          </p>
        )}

        {error && (
          <p className="text-xs text-red-500">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleFileButtonClick}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
              aria-label="Add photos"
            >
              <ImagePlus size={16} />
            </button>
            <button
              type="button"
              onClick={() => {
                setIsExpanded(true)
                setIsVideoOpen(v => !v)
              }}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
              aria-label="Add video"
            >
              <Video size={16} />
            </button>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              Posts are public and may be featured in CarSite content.
            </span>
          </div>
          <div className="flex items-center gap-2">
            {selectedCategories.length > 0 && (
              <div className="hidden sm:flex items-center gap-1 mr-2">
                {selectedCategories.slice(0, 3).map(c => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => removeSelectedCategory(c.slug)}
                    className="text-[11px] px-2 py-1 rounded-full border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
                    title="Remove category"
                  >
                    #{c.slug}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={resetForm}
              className="text-xs px-3 py-1.5 rounded-full border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || isUploading}
              className="btn btn-primary text-xs px-4 py-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isPending ? 'Submitting…' : 'Submit post'}
            </button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
      </form>
    </div>
  )
}

export default AuthorPostComposer
